// src/app/_service/space-directory.service.ts
// tasks/space_ux.md: a shared, offline-safe "every Space I can see, and its name" cache used
// everywhere the UI needs to show or filter by Space name — the Home filter/column/mobile card,
// the View note header/Technical details, and the Manage Spaces page. This is distinct from
// SpaceContextService (which tracks just ONE id — the active Space for new notes): this tracks the
// whole list, for display and filtering, not for deciding where a note gets saved.
//
// Offline-first: the last successful fetch is cached in localStorage (account-scoped, like
// SpaceContextService), so a Space's name still resolves correctly while offline instead of
// falling back to a raw id. refresh() is always fire-and-forget — a failed/offline refresh just
// keeps whatever was last cached (or empty, on a brand new device) rather than throwing.

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { Space, SpaceService } from './space.service';
import { AuthService } from './auth.service';
import { targetSuffix } from '../_util/data-target';

const SPACE_DIRECTORY_KEY_PREFIX = 'notes:space_directory';

@Injectable({ providedIn: 'root' })
export class SpaceDirectoryService {
  private readonly spacesSubject = new BehaviorSubject<Space[]>([]);
  readonly spaces$: Observable<Space[]> = this.spacesSubject.asObservable();

  constructor(
    private readonly spaceService: SpaceService,
    private readonly auth: AuthService,
  ) {
    this.spacesSubject.next(this.readCache());
  }

  private get storageKey(): string {
    return `${SPACE_DIRECTORY_KEY_PREFIX}${targetSuffix()}::${this.auth.getUsername() ?? 'anonymous'}`;
  }

  get spaces(): Space[] {
    return this.spacesSubject.value;
  }

  private readCache(): Space[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writeCache(spaces: Space[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(spaces));
    } catch {
      // Storage full/unavailable — the in-memory copy still works for this session.
    }
  }

  /** Re-seeds from this account's cache — call on login/account switch, same as
   * SpaceContextService.initialize(), so a different account never shows a stale prior
   * account's Spaces before its own refresh() completes. */
  loadCached(): void {
    this.spacesSubject.next(this.readCache());
  }

  /** Fire-and-forget refresh from the server. Never throws — a failed/offline fetch just leaves
   * the current (possibly stale, possibly empty) cache in place. Call after login/startup, and
   * after any action that changes the list (creating a Space, being added to/removed from one,
   * a Space being renamed) so the UI catches up without waiting for the next login. */
  refresh(): void {
    this.spaceService.getSpaces().subscribe({
      next: (spaces) => {
        this.spacesSubject.next(spaces);
        this.writeCache(spaces);
      },
      error: () => {
        // Offline-first: keep whatever was last cached rather than clearing it out.
      },
    });
  }

  /** The Space's display name, or a sensible fallback: 'Private' for a note with no spaceId at
   * all (pre-Spaces notes), or the raw id itself if the Space isn't in the cache yet (e.g. a
   * SHARED Space this device hasn't fetched the list for since joining). */
  getSpaceName(spaceId: string | null | undefined): string {
    if (!spaceId) return 'Private';
    const found = this.spaces.find((s) => s.id === spaceId);
    return found?.name ?? spaceId;
  }

  clear(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Ignored — clear() is best-effort housekeeping (e.g. on logout).
    }
    this.spacesSubject.next([]);
  }
}
